import {ApplicationStartupConfigurations} from '../config-interfaces/application-startup.configurations';
import {LauncherOverridesReader} from '../launcher-overrides-reader/launcher-overrides-reader';
import {IWindowService} from '../../services/window/window.service.interface';
import {IConfigurationEvaluationDataProvider} from '../../services/configurations/evaluation/configuration-evaluation-context/configuration-evaluation-data-provider';
import {PropertiesEvaluator} from '../../services/configurations/evaluation/properties-evaluator/properties-evaluator';
import {
    ConfigurationEvaluationContext
} from '../../services/configurations/evaluation/configuration-evaluation-context/configuration-evaluation-context';
import {ILoggerService} from '../../services/logger/logger.service.interface';
import {CGP_OBJECT_NAME} from '../constants';
import ISdkHooksProvider from './sdk-hooks-provider.interface';
import {IThemeDefinition} from "../../services/theme/theme-definition.interface";

const overrideJson = require('json-override');

export class SdkInitializer {
  constructor(private _applicationConfiguration: ApplicationStartupConfigurations,
              private _sdkHooksProvider: ISdkHooksProvider,
              private _launcherOverridesReader: LauncherOverridesReader,
              private _windowService: IWindowService,
              private _theme: IThemeDefinition,
              private _logger: ILoggerService) {

  }

  init(): Promise<void> {

    return import(/* webpackChunkName: "cgp-sdk" */ '../../../sdk/src/core/api').then(module => module.API).then(API => {
      window['LOG_LEVEL'] = this._applicationConfiguration.sdk.config.Logger.logLevel || 'INFO';
      const sdkApi = new API(this._applicationConfiguration.sdk.config);
      let sdkConfigOverrides: any = this.getSdkConfigOverrides();

      return new Promise<void>(resolve => {
        let onInitCompleteCallback  = () => {
          window[CGP_OBJECT_NAME] = sdkApi;
          resolve();
        };

        sdkApi.initialize(sdkConfigOverrides, onInitCompleteCallback);
      });
    });

  }


  private getSdkConfigOverrides() {
    let sdkConfigOverrides = {
      Country: this.getCountryServiceOverrides(),
      Skin: this.getSkinServiceOverrides(),
      Game: this.getGameServiceOverrides(),
      Integration: this.getIntegrationServiceOverrides(),
      Themes: this.getThemesServiceOverrides(),
      Http2: this.getHttp2ServiceOverrides(),
      JackpotToasters: this.getJackpotToastersServiceOverrides(),
      OrbitComponentRender: this.getOrbitComponentRenderOverrides(),
      CashierProxy: this.getCashierProxyOverrides(),
      GameRunnerProxy: this.getGameRunnerProxyOverrides()
    };

    overrideJson(sdkConfigOverrides, this._evaluateSdkConfigsInsideOrbit());
    return sdkConfigOverrides;
  }

  private getJackpotToastersServiceOverrides(): any {
    return {
      toasterImage: this._theme.shellTheme.images.jackpotWidget.generic.crown.toString()
    };
  }

  private getCountryServiceOverrides(): any {
    return {
      country: this._applicationConfiguration.country,
    };
  }

  private getSkinServiceOverrides(): any {

    const skinOverride = this._applicationConfiguration.orbit?.config?.SDK?.Skin || {};
    const sdkConfig = this._applicationConfiguration.sdk?.config;

    let responsibleGamblingLink = sdkConfig?.Skin?.responsibleGamblingLink;

    if(responsibleGamblingLink) {
      if (!this._windowService.isAbsolutUrl(responsibleGamblingLink)) { // it means that responsibleGamblingLink is a relative url
        let origin = this._windowService.origin;
        if (!origin.endsWith('/')) {
          origin = origin + '/';
        }
        let pathName = responsibleGamblingLink;
        if (pathName[0] === '/') {
          pathName = pathName.substring(1);
        }

        responsibleGamblingLink = origin + pathName;
      }

      skinOverride.responsibleGamblingLink = responsibleGamblingLink;
    }

    return skinOverride;
  }

  private _evaluateSdkConfigsInsideOrbit(): any {
    let sdkConfigsInsideOrbit = this._applicationConfiguration.orbit && this._applicationConfiguration.orbit.config && this._applicationConfiguration.orbit.config.SDK;
    if(!sdkConfigsInsideOrbit) {
      return {};
    }

    let segments: Record<string, unknown> = {
      env: this._applicationConfiguration.orbit.extraProperties.env
    };

    let configEvaluationDataProvider: IConfigurationEvaluationDataProvider = {
        configurationPath: this._applicationConfiguration.configurationPath,
        getSegmentValue: (segmentName => segments[segmentName]),
        getLauncherGames: () => [],
        admin: this._applicationConfiguration.admin,
        logger: this._logger
    };

    let context = new ConfigurationEvaluationContext(configEvaluationDataProvider);
    let evaluator = PropertiesEvaluator.fromRoot(sdkConfigsInsideOrbit, context);
    return evaluator.evaluate(context);
  }

  private getGameServiceOverrides(): any {

    let result: any = {
      resolveGameImagePath: (imageFilePath: string) => this._sdkHooksProvider.resolveGameImagePath(imageFilePath)
    };

    result.gameDefaultIcon = this._theme.mainContentTheme.images.gameDefaultIcon.toString();
    result.backgroundDefault = this._theme.mainContentTheme.images.gameDefaultBackground.toString();

    return result;
  }

  private _waitForMapperCGP = (resolve: (value?: unknown) => void) => {
    let mapperData = this._sdkHooksProvider.getGameTypesMappingForSdk();
    if(mapperData) {
      resolve(mapperData);
    } else {
      setTimeout(() => this._waitForMapperCGP(resolve), 5);
    }
  };

  private _gameTypeMapper() {
    return new Promise<any>(resolve => {
      this._waitForMapperCGP(resolve);
    });
  };

  private _getHeaderRegulationDataElement() {
    return new Promise<any>(resolve => {
      this._waitForHeaderRegulationDataElement(resolve);
    });
  };

  private _waitForHeaderRegulationDataElement = (resolve: (value?: unknown) => void) => {
    let headerRegulationDataElement = this._sdkHooksProvider.getHeaderRegulationDataElement();
    this.waitForElement(headerRegulationDataElement, resolve);
  };

  waitForElement(element: HTMLElement, resolve: (value?: unknown) => void) {
    if(element) {
      resolve(element);
    } else {
      setTimeout(() => this._waitForHeaderRegulationDataElement(resolve), 5);
    }
  }

  private getIntegrationServiceOverrides(): any {
    let launcherOverrides = this._launcherOverridesReader.getOverrides();

    return {
      gameTypeMapper: this._gameTypeMapper.bind(this),
      proxyLink: (url: string) => this._sdkHooksProvider.redirectProxy(url),
      openInNewWindowHook:  () => this._sdkHooksProvider.openInNewWindowHook(),
      isMiniCipFunc: () => this._sdkHooksProvider.isMiniCip(),
      isMiniCisFunc: () => this._sdkHooksProvider.isMiniCis(),
      headerRegulationDataElement: this._getHeaderRegulationDataElement.bind(this),
      CMSSource: launcherOverrides?.orbit?.cmsSource
    };
  }

  private getThemesServiceOverrides(): any {
      let sdkTheme = this._theme.shellTheme?.sdk;
      if (sdkTheme) {
          return sdkTheme;
      }
      return {}
  }

  private getHttp2ServiceOverrides(): any {
      let result: any = {};
      let launcherOverrides = this._launcherOverridesReader.getOverrides();
      if(launcherOverrides?.sdk?.api) {
        result.origin = launcherOverrides.sdk.api;
      }
      return result;
  }

  private getOrbitComponentRenderOverrides(): any {
    return {
      getOrbitComponentToSdkAdapter: () => this._sdkHooksProvider.getOrbitComponentToSdkAdapter()
    }
  }

  private getCashierProxyOverrides(): any {
    return {
      getCashierService: () => this._sdkHooksProvider.getCashierService()
    };
  }

  private getGameRunnerProxyOverrides(): any {
    return {
      getGameRunnerService: () => this._sdkHooksProvider.getGameRunnerService()
    };
  }

}



